import React, { ComponentType, Fragment, HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputBase } from '../../../../component-library';
import useLoqate from '../../../../hooks/useLoqate';
import { useAcquisitionContext } from '../../../../providers/AcquisitionProvider/AcquisitionProvider';
import { IPersonalInfo, IPersonalInfoErrors } from '../../../../providers/AcquisitionProvider/models';
import { useFeatureFlags } from '../../../../providers/FeatureFlagProvider/FeatureFlagProvider';
import { regexValidation, checkExistsDiacriticSpecialCharacters, wrapTrademarkSuperscript } from '../../../../utils/cob-utils';
import SessionStorage from '../../../../utils/SessionStorage';
import AddressSuggestionPopup from '../../../AddressSuggestionPopup/AddressSuggestionPopup';
import { addressValidationSemaphore } from '../../../PqDataEntryForm/util/addressValidationSemaphore';
import AddressField from '../../AddressFields/AddressField/AddressField';
import ApartmentField from '../../AddressFields/ApartmentField/ApartmentField';
import CityField from '../../AddressFields/CityField/CityField';
import StateField from '../../AddressFields/StateField/StateField';
import ZipCodeField from '../../AddressFields/ZipCodeField/ZipCodeField';
import { transformHTMLParserLinks } from '../../../../utils/rewriteLink/rewriteLink';

type TWrapperComponentProps = HTMLAttributes<HTMLElement> & Record<string, unknown>;

export interface IGroupedAddressFieldsHomePrimaryProps<TReactComponent extends keyof JSX.IntrinsicElements | ComponentType> {
    version: 'preApproved' | 'skipPQ' | 'skipPQ-Prefilled' | 'pqOfOne' | 'partnerHostedVersion' | 'versionD' | 'versionB' | 'versionF';
    addressType: 'mailing' | 'home';
    usePOBoxRegexCheck?: boolean;
    isMailingAddressHidden: boolean;
    setMailingCheckboxSelected: React.Dispatch<React.SetStateAction<boolean>>;
    isReadOnly?: boolean;
    setFirstEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    setSecondEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    wiDisclaimer?: string;
    wrapper?: {
        /**
         * Allows HTMLElements to be passed as the wrapper element for each field as a string value (i.e. 'div', 'p', etc...).
         * Allows Custom React Components to be passed as an Element (i.e. Col, Row, etc...).
         * Default will be React.ReactFragment and is not visible when rendered.
         */
        wrapperElement: TReactComponent;
        /**
         * Allows All HTMLAttributes to be passed as property.
         * Allows custom properties.
         */
        wrapperProps?: TWrapperComponentProps;
        /** 
         * Field Specific props.
         * Will override the generic wrapperProps.
         */
        addressWrapperProps?: TWrapperComponentProps;
        aptWrapperProps?: TWrapperComponentProps;
        cityWrapperProps?: TWrapperComponentProps;
        stateWrapperProps?: TWrapperComponentProps;
        zipWrapperProps?: TWrapperComponentProps;
    }
    suggestedAddressEnabled?: boolean;
    suggestedAddressHeadline?: string;
    suggestedAddressSubHeadline?: string;
    suggestedAddressAcceptCtaText?: string;
    suggestedAddressDeclineCtaText?: string;
    avcThreshold?: string;
    labelType?: 'mailing' | 'home';
    disclaimerMessage?: string;
}

interface IPrefillData {
    ADDRESS?: string;
    APT?: string;
    CITY?: string;
    STATE?: string;
    ZIP?: string;
}

const FIELDS = { ADDRESS: 'Address', APTUNIT: 'AptUnit', CITY: 'City', STATE: 'State', ZIP: 'Zip' };
type TFieldKeys = keyof typeof FIELDS
const STATES = {
    'ALABAMA': 'AL',
    'ALASKA': 'AK',
    'AMERICAN SAMOA': 'AS',
    'ARIZONA': 'AZ',
    'ARKANSAS': 'AR',
    'ARMED FORCES AFRICA': 'AE',
    'ARMED FORCES AMERICAS': 'AA',
    'ARMED FORCES PACIFIC': 'AP',
    'CALIFORNIA': 'CA',
    'COLORADO': 'CO',
    'CONNECTICUT': 'CT',
    'DC - WASHINGTON DC': 'DC',
    'DISTRICT OF COLUMBIA': 'DC',
    'DELAWARE': 'DE',
    'FEDERATED STATES OF MICRONESIA': 'FM',
    'FLORIDA': 'FL',
    'GEORGIA': 'GA',
    'GUAM': 'GU',
    'HAWAII': 'HI',
    'IDAHO': 'ID',
    'ILLINOIS': 'IL',
    'INDIANA': 'IN',
    'IOWA': 'IA',
    'KANSAS': 'KS',
    'KENTUCKY': 'KY',
    'LOUISIANA': 'LA',
    'MAINE': 'ME',
    'MARSHALL ISLAND': 'MH',
    'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA',
    'MICHIGAN': 'MI',
    'MINNESOTA': 'MN',
    'MISSISSIPPI': 'MS',
    'MISSOURI': 'MO',
    'MONTANA': 'MT',
    'NEBRASKA': 'NE',
    'NEVADA': 'NV',
    'NEW HAMPSHIRE': 'NH',
    'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM',
    'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC',
    'NORTH DAKOTA': 'ND',
    'NORTHERN MARIANA ISLANDS': 'MP',
    'OHIO': 'OH',
    'OKLAHOMA': 'OK',
    'OREGON': 'OR',
    'PALAU': 'PW',
    'PENNSYLVANIA': 'PA',
    'PUERTO RICO': 'PR',
    'RHODE ISLAND': 'RI',
    'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD',
    'TENNESSEE': 'TN',
    'TEXAS': 'TX',
    'UTAH': 'UT',
    'VERMONT': 'VT',
    'VIRGIN ISLANDS': 'VI',
    'VIRGINIA': 'VA',
    'WASHINGTON': 'WA',
    'WEST VIRGINIA': 'WV',
    'WISCONSIN': 'WI',
    'WYOMING': 'WY',
};

const GroupedAddressFieldsHomePrimary = <T extends keyof JSX.IntrinsicElements | ComponentType<any>>({
    wrapper = {
        wrapperElement: Fragment as any,
        wrapperProps: {},
        addressWrapperProps: {},
        aptWrapperProps: {},
        cityWrapperProps: {},
        stateWrapperProps: {},
        zipWrapperProps: {},
    },
    ...props
}: IGroupedAddressFieldsHomePrimaryProps<T>) => {
    const { personalInfoForm, setPersonalInfoForm,
        setPersonalInfoErrors,
        setHomeAddressValidateOnSubmit, isApplicationInfoRecieved, homeAddressValidateOnSubmit, validationRef } = useAcquisitionContext();
    const regexContainer = regexValidation();
    const { getFeatureFlags } = useFeatureFlags();
    const addressInputRef = useRef<HTMLInputElement>();
    const aptRef = useRef<HTMLInputElement>();
    const cityRef = useRef<HTMLInputElement>();
    const stateRef = useRef<HTMLInputElement>();
    const zipRef = useRef<HTMLInputElement>();
    const loqateAutocompleteToggle = getFeatureFlags('DPM-12391-Loqate-Autocomplete-Check');

    const [loqateController, setLoqateController] = useState<any>();
    const [showPopup, setShowPopup] = useState(false);
    const [loqateResponse, setLoqateResponse] = useState<any>({});
    const loqateIsValidating = useRef(false);
    const [stateResetToggle, setStateResetToggle] = useState(false);
    const { initAutoComplete, setBasicListeners, getAddressData, pca, loqateEnabled, loqateKey, addressCacheRef, generateCacheKey, addToCache, setPopupShown, popupShown } = useLoqate();

    /*
        DPM-13680: Semantic field type is used for business logic conditions, needed for swapping logic in this ticket. fieldType/addressType used for form state changes and access.
    */
    const semanticFieldType = props.addressType === 'mailing' ? 'home' : 'mailing'

    const FIELD_NAMES = useMemo(
        () => Object.fromEntries(
            Object.entries(FIELDS).map(e => [e[0], props.addressType + e[1]]),
        ) as Record<TFieldKeys, keyof Partial<Omit<IPersonalInfo, 'homeAddressShown'>>>, [props.addressType],
    );

    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    const retrieveStateAbbreviation = useCallback((state: string): string => state ? STATES[state.toUpperCase()] : undefined, []);

    const homeAddressErrorText = personalInfoForm.mailingAddress.value && regexContainer.po_box_test.test(personalInfoForm.mailingAddress.value) ? 'Home Address cannot be a PO Box' : 'Please provide a valid Home Street Address';
    const mailingAddressErrorText = `Please provide a valid ${capitalize(props.labelType)} Address`;

    const toggleFieldAndPushdownProviderState = (formFieldName: string, errorFieldName: string, isError: 'None' | 'RequiredField' | 'NotValid') => {
        const targetObject = personalInfoForm[formFieldName];
        setPersonalInfoForm((prev: IPersonalInfo) => ({ ...prev, [formFieldName]: { ...targetObject, valid: isError === 'None' } }));
        setPersonalInfoErrors((prev: IPersonalInfoErrors) => ({ ...prev, [errorFieldName]: isError }));
    };

    const clearPersonalInformationData = (fieldType: 'mailing' | 'home', valid: boolean = false) => {
        setPersonalInfoForm((personalInfoForm: any) => ({
            ...personalInfoForm,
            [`${fieldType}Address`]: { value: '', valid: valid },
            [`${fieldType}AptUnit`]: { value: '', valid: true },
            [`${fieldType}City`]: { value: '', valid: valid },
            [`${fieldType}State`]: { value: '', valid: valid },
            [`${fieldType}Zip`]: { value: '', valid: valid },
            //This might need to be removed since its not based on...., mailing address...
            homeAddressShown: semanticFieldType === 'home' ? personalInfoForm.homeAddressShown : false,
        }));
    };

    const closeAndResetHomeFields = () => {
        setHomeAddressValidateOnSubmit(false);
        setPersonalInfoForm((personalInfoForm: any) => ({
            ...personalInfoForm,
            homeAddress: { value: '', valid: true },
            homeAptUnit: { value: '', valid: true },
            homeCity: { value: '', valid: true },
            homeState: { value: '', valid: true },
            homeZip: { value: '', valid: true },
            homeAddressShown: false,
        }));
        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
            return {
                ...prev,
                HomeAddressLine1: 'None',
                HomeCity: 'None',
                HomeState: 'None',
                HomeZipCode: 'None',
                HomeAddrAndAptNumMatch: 'None',
            };
        });
    };

    const handleValidFieldStates = (fieldType, isCommercial, addressObject) => {

        // Home Address cannot be commercial
        if (semanticFieldType === 'home' && isCommercial) {
            clearPersonalInformationData(fieldType);
            setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                return {
                    ...prev,
                    [`${capitalize(fieldType)}AddressLine1`]: 'RequiredField',
                    [`${capitalize(fieldType)}City`]: 'RequiredField',
                    [`${capitalize(fieldType)}State`]: 'RequiredField',
                    [`${capitalize(fieldType)}ZipCode`]: 'RequiredField',
                };
            });
            return;
        }

        // Mailing Address can be commercial
        if (semanticFieldType === 'mailing') {
            if (isCommercial) {
                setPersonalInfoForm((prev) => ({ ...prev, homeAddressShown: true }));
                setHomeAddressValidateOnSubmit(true);
            }
        }

        setPersonalInfoForm((personalInfoForm) => {
            return {
                ...personalInfoForm,
                [`${fieldType}Address`]: { value: addressObject.addressLine1, valid: true },
                [`${fieldType}AptUnit`]: { value: addressObject.addressLine2, valid: true },
                [`${fieldType}City`]: { value: addressObject.city, valid: true },
                [`${fieldType}State`]: { value: addressObject.state, valid: true },
                [`${fieldType}Zip`]: { value: addressObject.zipCode, valid: true },
            };
        });

        // Remove any errors
        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
            const personalInfoErrorsUpdate = {
                ...prev,
                [`${capitalize(fieldType)}AddressLine1`]: 'None',
                [`${capitalize(fieldType)}City`]: 'None',
                [`${capitalize(fieldType)}State`]: 'None',
                [`${capitalize(fieldType)}ZipCode`]: 'None',
            };

            verifyAddressAndAptDoNotMatch();

            return personalInfoErrorsUpdate;
        });
    }

    const handleInvalidFieldStates = (fieldType) => {

        clearPersonalInformationData(fieldType);
        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
            return {
                ...prev,
                [`${capitalize(fieldType)}AddressLine1`]: 'RequiredField',
                [`${capitalize(fieldType)}City`]: 'RequiredField',
                [`${capitalize(fieldType)}State`]: 'RequiredField',
                [`${capitalize(fieldType)}ZipCode`]: 'RequiredField',
            };
        });
    }

    const handleAcceptCta = (fieldType) => {
        handleValidFieldStates(fieldType, loqateResponse.commercial, loqateResponse.suggestedAddress);

        if (validationRef.current) {
            validationRef.current.isValid = true;
            validationRef.current.data = { commercial: loqateResponse.commercial, valid: true, suggestedAddress: loqateResponse.suggestedAddress };
        }

        setShowPopup(false);
    }

    const handleDeclineCta = (fieldType) => {
        handleInvalidFieldStates(fieldType);
        setShowPopup(false);
        addressInputRef.current.focus();
    }

    const validateField = (
        personalInfoFormKey: keyof Omit<IPersonalInfo, 'homeAddressShown'>,
        personalInfoErrorsKey: keyof IPersonalInfoErrors,
        allowRegex?: RegExp | null,
        disallowRegex?: any,
        isRequired: boolean = true,
        fieldValidationCallback?: Function,
    ) => {
        const personalInfoFormObject = personalInfoForm[personalInfoFormKey];
        const isEmpty = personalInfoFormObject?.value?.length <= 0;

        if (isEmpty && !isRequired) {
            return;
        }

        const isAllowed = (allowRegex ? allowRegex.test(personalInfoFormObject?.value) : true) && (fieldValidationCallback ? fieldValidationCallback() : true);
        const isNotAllowed = (disallowRegex ? disallowRegex.test(personalInfoFormObject?.value) : false) && checkExistsDiacriticSpecialCharacters(personalInfoFormObject?.value);
        const isHomePrimaryAddressPOBox = personalInfoFormKey === 'mailingAddress' && regexContainer.po_box_test.test(personalInfoForm.mailingAddress.value) || personalInfoForm.mailingAddress?.value?.toUpperCase() === 'GENERAL DELIVERY';

        if (isEmpty || !isAllowed) {
            toggleFieldAndPushdownProviderState(personalInfoFormKey, personalInfoErrorsKey, 'RequiredField');
        } else if (isNotAllowed || isHomePrimaryAddressPOBox) {
            toggleFieldAndPushdownProviderState(personalInfoFormKey, personalInfoErrorsKey, 'NotValid');
        } else {
            toggleFieldAndPushdownProviderState(personalInfoFormKey, personalInfoErrorsKey, 'None');
        }

        return !isEmpty && isAllowed && !isNotAllowed && !isHomePrimaryAddressPOBox;
    };

    const verifyAddressAndAptDoNotMatch = () => {
        const addressValue = personalInfoForm[`${props.addressType}Address`]?.value;
        const aptValue = personalInfoForm[`${props.addressType}AptUnit`]?.value;
        const errorFieldName = semanticFieldType === 'home' ? 'AddrAndAptNumMatch' : 'HomeAddrAndAptNumMatch';

        if (addressValue?.length && aptValue?.length && (addressValue.toLowerCase() === aptValue.toLowerCase())) {
            toggleFieldAndPushdownProviderState(`${props.addressType}AptUnit`, errorFieldName, 'NotValid');
            return false;
        }

        toggleFieldAndPushdownProviderState(`${props.addressType}AptUnit`, errorFieldName, 'None');
        return true;
    };

    const validateAddressWithLoqate = async (addressLine1?: string, addressLine2?: string, city?: string, state?: string, zip?: string, type?: string) => {
        return await getAddressData(addressLine1, addressLine2, city, state, zip, type, props.suggestedAddressEnabled, props.avcThreshold);
    };

    const validateFullAddress = async () => {

        let addressValidationResponse = { valid: true, commercial: false, suggestedAddress: undefined };

        const fieldType = props.addressType;

        const personalInfo = JSON.parse(JSON.stringify(personalInfoForm));
        const allAddressValues = [personalInfo[`${fieldType}Address`].value, personalInfo[`${fieldType}AptUnit`].value, personalInfo[`${fieldType}City`].value, personalInfo[`${fieldType}State`].value, personalInfo[`${fieldType}Zip`].value];
        const requiredAddressValues = [personalInfo[`${fieldType}Address`].value, personalInfo[`${fieldType}City`].value, personalInfo[`${fieldType}State`].value, personalInfo[`${fieldType}Zip`].value];

        const hasAllRequiredFields = requiredAddressValues.every(Boolean);

        const isAptFieldPOBox = regexContainer.po_box_test.test(personalInfo[FIELD_NAMES.APTUNIT].value) || personalInfo[FIELD_NAMES.APTUNIT]?.value?.toUpperCase() === 'GENERAL DELIVERY';
        const isAddressFieldPOBox = regexContainer.po_box_test.test(personalInfo[FIELD_NAMES.ADDRESS].value) || personalInfo[FIELD_NAMES.ADDRESS]?.value?.toUpperCase() === 'GENERAL DELIVERY';

        if (semanticFieldType === 'home' && (isAddressFieldPOBox || isAptFieldPOBox)) {
            toggleFieldAndPushdownProviderState('mailingAddress', 'MailingAddressLine1', 'RequiredField');
        }

        if (!hasAllRequiredFields) {
            return;
        }

        //Has all requiredFields, generate the cacheKey
        await addressValidationSemaphore.acquire();
        const cacheKey = generateCacheKey(fieldType, allAddressValues);
        const cache = addressCacheRef.current;
        //if addressCache exists, check its data, else run normal validation
        if (cache[cacheKey]) {
            addressValidationResponse = { valid: cache[cacheKey].isValid, commercial: cache[cacheKey].isPOBoxOrCommercial, suggestedAddress: cache[cacheKey].suggestedAddress };
        } else if (loqateEnabled && (loqateAutocompleteToggle || (!loqateAutocompleteToggle && !document.querySelector('.pcaselected:hover'))) && !cache[cacheKey]) {
            //Using semanticFieldType because loqate changes validation results based on passed address type
            addressValidationResponse = await validateAddressWithLoqate(...allAddressValues, semanticFieldType);
            addToCache(cacheKey, { address: cacheKey, isValid: addressValidationResponse.valid, isPOBoxOrCommercial: addressValidationResponse.commercial, data: addressValidationResponse });
        }
        addressValidationSemaphore.release();

        if (addressValidationResponse?.valid) {
            handleValidFieldStates(fieldType, addressValidationResponse.commercial, {
                addressLine1: personalInfoForm[`${fieldType}Address`].value,
                addressLine2: personalInfoForm[`${fieldType}AptUnit`].value,
                city: personalInfoForm[`${fieldType}City`].value,
                state: personalInfoForm[`${fieldType}State`].value,
                zipCode: personalInfoForm[`${fieldType}Zip`].value,
            });
        } else {
            if (props.suggestedAddressEnabled && addressValidationResponse.suggestedAddress && !popupShown) {
                setLoqateResponse(addressValidationResponse);
                setPopupShown(true);
                setShowPopup(true);
            } else {
                handleInvalidFieldStates(fieldType);
            }
        }
        if (validationRef.current) {
            validationRef.current.isValid = addressValidationResponse.valid;
            validationRef.current.data = addressValidationResponse;
        }
    };

    /*
        Initialize the loqate controller in state
        Uses new toggle state isMailingAddressHidden because its tied to user interaction
    */
    useEffect(() => {
        const fieldType = props.addressType;
        if (pca && loqateEnabled && (fieldType === 'mailing' || !props.isMailingAddressHidden)) {
            setLoqateController(initAutoComplete(
                `${fieldType}AddressLine1`,
                `${fieldType}AddressLine2`,
                `${fieldType}AddressCity`,
                `${fieldType}AddressState`,
                `${fieldType}AddressZip`,
            ));
        }
    }, [pca, loqateEnabled, props.isMailingAddressHidden]);

    // sets home address to required when home address pops up
    useEffect(() => {
        if (homeAddressValidateOnSubmit === true) {
            setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                return {
                    ...prev,
                    HomeAddressLine1: 'RequiredField',
                    HomeCity: 'RequiredField',
                    HomeState: 'RequiredField',
                    HomeZipCode: 'RequiredField',
                };
            });
        }
    }, [homeAddressValidateOnSubmit]);

    // Load and create listener if/when control changes
    useEffect(() => {
        if (loqateController) {
            loqateController.load();
            setBasicListeners(loqateController);
            // loqate autofill, always assume valid.
            // config filters out invalid data for POBox and/or commercial
            loqateController.listen('populate', async (address: any) => {
                if (loqateAutocompleteToggle) {
                    const cache = addressCacheRef.current;
                    const cacheKey = generateCacheKey(props.addressType, [
                        address.FormattedLine1,
                        address.SubBuilding,
                        address.City,
                        address.Province,
                        address.PostalCode
                    ]);
                    let data;
                    if (cache[cacheKey]) {
                        data = cache[cacheKey].data;
                    } else {
                        data = await getAddressData(
                            address.FormattedLine1,
                            address.SubBuilding,
                            address.City,
                            address.Province,
                            address.PostalCode,
                            semanticFieldType
                        );
                    }

                    addToCache(cacheKey, { address: cacheKey, isValid: data.valid, isPOBoxOrCommercial: data.commercial, data: data });

                    if (data?.valid) {
                        setPersonalInfoForm((personalInfoForm: any) => ({
                            ...personalInfoForm,
                            [`${props.addressType}AptUnit`]: { value: address.SubBuilding, valid: true },
                            [`${props.addressType}Address`]: { value: address.FormattedLine1, valid: true },
                            [`${props.addressType}City`]: { value: address.City, valid: true },
                            [`${props.addressType}State`]: { value: address.Province, valid: true },
                            [`${props.addressType}Zip`]: { value: address.PostalCode, valid: true },
                        }));

                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                            const personalInfoErrorsUpdate = {
                                ...prev,
                                [`${capitalize(props.addressType)}AddressLine1`]: 'None',
                                [`${capitalize(props.addressType)}City`]: 'None',
                                [`${capitalize(props.addressType)}State`]: 'None',
                                [`${capitalize(props.addressType)}ZipCode`]: 'None',
                            };

                            if (semanticFieldType === 'home') {
                                personalInfoErrorsUpdate.AddrAndAptNumMatch = 'None';
                            } else {
                                personalInfoErrorsUpdate.HomeAddrAndAptNumMatch = 'None';
                            }

                            return personalInfoErrorsUpdate;
                        });
                        validationRef.current.isValid = true;
                        validationRef.current.data = data;
                    } else {
                        setPersonalInfoForm((personalInfoForm: any) => ({
                            ...personalInfoForm,
                            [`${props.addressType}AptUnit`]: { value: address.SubBuilding, valid: false },
                            [`${props.addressType}Address`]: { value: address.FormattedLine1, valid: false },
                            [`${props.addressType}City`]: { value: address.City, valid: false },
                            [`${props.addressType}State`]: { value: address.Province, valid: false },
                            [`${props.addressType}Zip`]: { value: address.PostalCode, valid: false },
                        }));

                        clearPersonalInformationData(props.addressType);
                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                            return {
                                ...prev,
                                [`${capitalize(props.addressType)}AddressLine1`]: 'RequiredField',
                                [`${capitalize(props.addressType)}City`]: 'RequiredField',
                                [`${capitalize(props.addressType)}State`]: 'RequiredField',
                                [`${capitalize(props.addressType)}ZipCode`]: 'RequiredField',
                            };
                        });
                    }
                }
                else {

                    //Adding basic PO box form validations with semantic logic. This probably should have already existed in the original grouped address fields
                    const personalInfo: unknown = JSON.parse(JSON.stringify(personalInfoForm));
                    const isAptFieldPOBox = regexContainer.po_box_test.test(personalInfo[FIELD_NAMES.APTUNIT].value) || personalInfo[FIELD_NAMES.APTUNIT]?.value?.toUpperCase() === 'GENERAL DELIVERY';
                    const isAddressFieldPOBox = regexContainer.po_box_test.test(personalInfo[FIELD_NAMES.ADDRESS].value) || personalInfo[FIELD_NAMES.ADDRESS]?.value?.toUpperCase() === 'GENERAL DELIVERY';

                    if (semanticFieldType === 'home' && (isAddressFieldPOBox || isAptFieldPOBox)) {
                        setPersonalInfoForm((personalInfoForm: IPersonalInfo) => ({
                            ...personalInfoForm,
                            [`${props.addressType}AptUnit`]: { value: address.SubBuilding, valid: false },
                            [`${props.addressType}Address`]: { value: address.FormattedLine1, valid: false },
                            [`${props.addressType}City`]: { value: address.City, valid: false },
                            [`${props.addressType}State`]: { value: address.Province, valid: false },
                            [`${props.addressType}Zip`]: { value: address.PostalCode, valid: false },
                        }));

                        clearPersonalInformationData(props.addressType);
                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                            return {
                                ...prev,
                                [`${capitalize(props.addressType)}AddressLine1`]: 'RequiredField',
                                [`${capitalize(props.addressType)}City`]: 'RequiredField',
                                [`${capitalize(props.addressType)}State`]: 'RequiredField',
                                [`${capitalize(props.addressType)}ZipCode`]: 'RequiredField',
                            };
                        });
                    } else {
                        setPersonalInfoForm((personalInfoForm: IPersonalInfo) => ({
                            ...personalInfoForm,
                            [`${props.addressType}AptUnit`]: { value: address.SubBuilding, valid: true },
                            [`${props.addressType}Address`]: { value: address.FormattedLine1, valid: true },
                            [`${props.addressType}City`]: { value: address.City, valid: true },
                            [`${props.addressType}State`]: { value: address.Province, valid: true },
                            [`${props.addressType}Zip`]: { value: address.PostalCode, valid: true },
                        }));

                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                            const personalInfoErrorsUpdate = {
                                ...prev,
                                [`${capitalize(props.addressType)}AddressLine1`]: 'None',
                                [`${capitalize(props.addressType)}City`]: 'None',
                                [`${capitalize(props.addressType)}State`]: 'None',
                                [`${capitalize(props.addressType)}ZipCode`]: 'None',
                            };

                            if (semanticFieldType === 'home') {
                                personalInfoErrorsUpdate.AddrAndAptNumMatch = 'None';
                            } else {
                                personalInfoErrorsUpdate.HomeAddrAndAptNumMatch = 'None';
                            }

                            return personalInfoErrorsUpdate;
                        });
                    }
                }
            });
        }
    }, [loqateController]);

    // prefill validation
    useEffect(() => {
        const getFullApplicationPrefillData = () => {
            if (props.version !== 'skipPQ' && props.version !== 'skipPQ-Prefilled' && props.version !== 'versionD') {
                return [null, null];
            }

            const prefillDataElement = document.getElementById('skip-pre-post-data');

            if (!prefillDataElement?.dataset) {
                return [null, null];
            }

            return [{
                ADDRESS: prefillDataElement?.dataset?.address,
                APT: prefillDataElement?.dataset?.apartment,
                CITY: prefillDataElement?.dataset?.city,
                STATE: prefillDataElement?.dataset?.state,
                ZIP: prefillDataElement?.dataset?.zipCode,
            }, {
                ADDRESS: prefillDataElement?.dataset?.homeAddress,
                APT: prefillDataElement?.dataset?.homeApartment,
                CITY: prefillDataElement?.dataset?.homeCity,
                STATE: prefillDataElement?.dataset?.homeState,
                ZIP: prefillDataElement?.dataset?.homeZipCode,
            }];
        };

        const getPreApprovedPrefillData = async () => {
            if (props.version !== 'preApproved' || !SessionStorage.verify('pre-approval-data')) {
                return [null, null];
            }

            const prefillDataElement = await SessionStorage.getCipher('pre-approval-data', true);

            if (!prefillDataElement?.Applicant) {
                return;
            }

            return [{
                ADDRESS: prefillDataElement?.Applicant?.MailingAddress?.AddressLineOne,
                APT: prefillDataElement?.Applicant?.MailingAddress?.AddressLineTwo,
                CITY: prefillDataElement?.Applicant?.MailingAddress?.City,
                STATE: prefillDataElement?.Applicant?.MailingAddress?.State,
                ZIP: prefillDataElement?.Applicant?.MailingAddress?.ZipCode,
            }, {
                ADDRESS: prefillDataElement?.Applicant?.HomeAddress?.AddressLineOne,
                APT: prefillDataElement?.Applicant?.HomeAddress?.AddressLineTwo,
                CITY: prefillDataElement?.Applicant?.HomeAddress?.City,
                STATE: prefillDataElement?.Applicant?.HomeAddress?.State,
                ZIP: prefillDataElement?.Applicant?.HomeAddress?.ZipCode,
            }];
        };

        const clearPreApprovedAddressPrefillData = async (mailing = true, home = true) => {
            if (props.version === 'preApproved' && SessionStorage.verify('pre-approval-data')) {
                const prefillData = await SessionStorage.getCipher('pre-approval-data', true);
                if (mailing) {
                    prefillData.Applicant.MailingAddress = {};
                }

                if (home) {
                    prefillData.Applicant.HomeAddress = {};
                }
                await SessionStorage.setCipher('pre-approval-data', prefillData);
            }
        };

        const prefillValidationWithLoqate = async () => {

            /*
                Form state access will be for MailingAddress
                Semantically, it is the home address fields, so validations follow rules for home address
            */
            const validateAndSetPrimaryHomeAddress = async (homeAddress) => {
                homeAddress.STATE = retrieveStateAbbreviation(homeAddress?.STATE) || homeAddress?.STATE?.toUpperCase() || '';

                let isHomeAddressDataIncomplete = Object.keys(homeAddress).some(key => key === 'APT' ? false : !homeAddress[key]);
                const isPOBoxRegexCheck = !!props.usePOBoxRegexCheck && (regexContainer.po_box_test.test(homeAddress.APT) || homeAddress.APT?.toUpperCase() === 'GENERAL DELIVERY');
                const isAddressFieldPOBox = regexContainer.po_box_test.test(homeAddress.ADDRESS) || homeAddress.ADDRESS?.toUpperCase() === 'GENERAL DELIVERY';

                if (isPOBoxRegexCheck) {
                    isHomeAddressDataIncomplete = true;
                }

                let loqateResult = { valid: !isHomeAddressDataIncomplete, commercial: false };

                //Hard coded to run home address validations
                if (loqateKey.current && !isHomeAddressDataIncomplete) {
                    loqateResult = await validateAddressWithLoqate(
                        homeAddress?.ADDRESS,
                        homeAddress?.APT,
                        homeAddress?.CITY,
                        homeAddress?.STATE,
                        String(homeAddress?.ZIP)?.substring(0, 5),
                        'home',
                    );
                }

                if (!loqateResult?.valid || loqateResult?.commercial || isPOBoxRegexCheck || isAddressFieldPOBox) {

                    clearPersonalInformationData(props.addressType, true);

                    if (props.version === 'skipPQ-Prefilled' || props.version === 'preApproved') {
                        props.setFirstEdit && props.setFirstEdit(false);
                    }

                    //clear out everything
                    await clearPreApprovedAddressPrefillData(false, true);

                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                        return {
                            ...prev,
                            MailingAddressLine1: 'RequiredField',
                            MailingCity: 'RequiredField',
                            MailingState: 'RequiredField',
                            MailingZipCode: 'RequiredField',
                        };
                    });
                } else { // valid
                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        mailingAddress: { value: homeAddress?.ADDRESS?.trim() ?? '', valid: true },
                        mailingAptUnit: { value: homeAddress?.APT?.trim() ?? '', valid: true },
                        mailingCity: { value: homeAddress?.CITY?.trim() ?? '', valid: true },
                        mailingState: { value: homeAddress?.STATE?.trim() ?? '', valid: true },
                        mailingZip: { value: String(homeAddress?.ZIP ?? '')?.substring(0, 5)?.trim(), valid: true },
                    }));

                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                        return {
                            ...prev,
                            MailingAddressLine1: 'None',
                            MailingCity: 'None',
                            MailingState: 'None',
                            MailingZipCode: 'None',
                            AddrAndAptNumMatch: 'None'
                        };
                    });
                }

                if (validationRef.current) {
                    validationRef.current.isValid = loqateResult.valid;
                    validationRef.current.data = loqateResult;
                }
            };

            /*
                Form state access will be for HomeAddress
                Semantically, it is the mailing address fields, so validations follow rules for mailing address
            */
            const validateAndSetSecondaryMailingAddress = async (mailingAddress) => {
                mailingAddress.STATE = retrieveStateAbbreviation(mailingAddress?.STATE) || mailingAddress?.STATE?.toUpperCase() || '';
                const isMailingAddressDataIncomplete = Object.keys(mailingAddress).some(key => key === 'APT' ? false : !mailingAddress[key]);

                let loqateResult = { valid: !isMailingAddressDataIncomplete, commercial: false };

                if (loqateKey.current && !isMailingAddressDataIncomplete) {
                    loqateResult = await validateAddressWithLoqate(
                        mailingAddress?.ADDRESS,
                        mailingAddress?.APT,
                        mailingAddress?.CITY,
                        mailingAddress?.STATE,
                        String(mailingAddress?.ZIP)?.substring(0, 5),
                        'mailing',
                    );
                }

                if (!loqateResult?.valid) {

                    clearPersonalInformationData(props.addressType, true);

                    if (props.version === 'skipPQ-Prefilled' || props.version === 'preApproved') {
                        props.setFirstEdit && props.setFirstEdit(false);
                    }

                    await clearPreApprovedAddressPrefillData();

                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                        return {
                            ...prev,
                            HomeAddressLine1: 'RequiredField',
                            HomeCity: 'RequiredField',
                            HomeState: 'RequiredField',
                            HomeZipCode: 'RequiredField',
                        };
                    });
                } else { // valid
                    const isPOBoxRegexCheck = !!props.usePOBoxRegexCheck && (regexContainer.po_box_test.test(mailingAddress.APT) || mailingAddress.APT?.toUpperCase() === 'GENERAL DELIVERY');
                    const isAddressFieldPOBox = regexContainer.po_box_test.test(mailingAddress.ADDRESS) || mailingAddress.ADDRESS?.toUpperCase() === 'GENERAL DELIVERY';

                    if (isPOBoxRegexCheck || isAddressFieldPOBox || loqateResult?.commercial) {
                        setPersonalInfoForm((prev) => ({ ...prev, homeAddressShown: true }));
                        setHomeAddressValidateOnSubmit(true);
                        //We need Mailing Address to be non commercial, and its required.
                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                            return {
                                ...prev,
                                MailingAddressLine1: 'RequiredField',
                                MailingCity: 'RequiredField',
                                MailingState: 'RequiredField',
                                MailingZipCode: 'RequiredField',
                            };
                        });
                    }

                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        homeAddress: { value: mailingAddress?.ADDRESS?.trim() ?? '', valid: true },
                        homeAptUnit: { value: mailingAddress?.APT?.trim() ?? '', valid: true },
                        homeCity: { value: mailingAddress?.CITY?.trim() ?? '', valid: true },
                        homeState: { value: mailingAddress?.STATE?.trim() ?? '', valid: true },
                        homeZip: { value: String(mailingAddress?.ZIP ?? '')?.substring(0, 5)?.trim(), valid: true },
                    }));

                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                        return {
                            ...prev,
                            HomeAddressLine1: 'None',
                            HomeCity: 'None',
                            HomeState: 'None',
                            HomeZipCode: 'None',
                            HomeAddrAndAptNumMatch: 'None',
                        };
                    });
                }

                if (validationRef.current) {
                    validationRef.current.isValid = loqateResult.valid;
                    validationRef.current.data = loqateResult;
                }
            }

            let mailingAddress: IPrefillData = {};
            let homeAddress: IPrefillData = {};

            if (props.version === 'skipPQ' || props.version === 'skipPQ-Prefilled' || props.version === 'versionD') {
                [mailingAddress, homeAddress] = getFullApplicationPrefillData();
            } else if (props.version === 'preApproved' && SessionStorage.verify('pre-approval-data')) {
                [mailingAddress, homeAddress] = await getPreApprovedPrefillData();
            }

            if (homeAddress && Object.values(homeAddress).join('').length) {

                props.setMailingCheckboxSelected(true);

                setPersonalInfoForm((prev) => ({ ...prev, homeAddressShown: true }));
                setHomeAddressValidateOnSubmit(true);

                setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                    return {
                        ...prev,
                        HomeAddressLine1: 'RequiredField',
                        HomeCity: 'RequiredField',
                        HomeState: 'RequiredField',
                        HomeZipCode: 'RequiredField',
                    };
                });
            }

            if (semanticFieldType === 'home' && mailingAddress) {
                await validateAndSetPrimaryHomeAddress(mailingAddress);
            }

            if (semanticFieldType === 'mailing' && homeAddress && Object.values(homeAddress).join('').length && !props.isMailingAddressHidden) {
                await validateAndSetSecondaryMailingAddress(homeAddress);
            }

        };

        const hasSavedAppData = SessionStorage.verifyNotEmpty('preApprovalFullAppData') || SessionStorage.verifyNotEmpty('prequalFullAppData');

    

        if (isApplicationInfoRecieved && !loqateIsValidating.current && !hasSavedAppData) {
            loqateIsValidating.current = true;
            prefillValidationWithLoqate();
        }
    }, [isApplicationInfoRecieved]);

    useEffect(() => {
        //Fix for InputDropdown not resetting interaction states when hiding and showing Mailing Address
        setStateResetToggle((toggle) => !toggle);
        if (props.isMailingAddressHidden) {
            closeAndResetHomeFields();
        } else {
            setPersonalInfoForm((prev) => ({ ...prev, homeAddressShown: true }));
            setHomeAddressValidateOnSubmit(true);
        }
    }, [props.isMailingAddressHidden])

    if (props?.isReadOnly) {
        return (
            <>
                <InputBase
                    alternate
                    className='h-auto'
                    disabled
                    inputClass='PersonalInformationForm__input--locked bg-white font-bold text-grey-tundora py-0 px-1'
                    label={`${capitalize(props.labelType)} Address`}
                    readOnly
                    noErrorsOrHelper
                    setValue={() => { }}
                    required
                    value={personalInfoForm[FIELD_NAMES.ADDRESS].value + ' ' + personalInfoForm[FIELD_NAMES.APTUNIT].value}
                />
                <InputBase
                    alternate
                    className='h-auto'
                    disabled
                    inputClass='PersonalInformationForm__input--locked bg-white font-bold text-grey-tundora py-0 px-1'
                    aria-label={`${capitalize(props.labelType)} Address Line Two`}
                    readOnly
                    setValue={() => { }}
                    value={personalInfoForm[FIELD_NAMES.CITY].value + ' ' + personalInfoForm[FIELD_NAMES.STATE].value + ' ' + personalInfoForm[FIELD_NAMES.ZIP].value}
                />
            </>
        );
    }

    // bypass ts(2604) error which would otherwise require an extensive type definition using union
    const Wrapper = wrapper.wrapperElement as any;
    return (
        <>
            <Wrapper {...{ ...wrapper.wrapperProps, ...wrapper.addressWrapperProps }}>
                <AddressField key='AddressField'
                    addressType={props.addressType}
                    alternate
                    className='block'
                    error={!personalInfoForm[FIELD_NAMES.ADDRESS].valid}
                    errorText={props.labelType === 'mailing' ? mailingAddressErrorText : homeAddressErrorText}
                    requiredMessage={props.labelType === 'mailing' ? mailingAddressErrorText : homeAddressErrorText}
                    id={`${props.addressType}AddressLine1`}
                    label={`${capitalize(props.labelType)} Address`}
                    maxLength={100}
                    onBlurCallback={() => {
                        validateField(
                            FIELD_NAMES.ADDRESS,
                            `${capitalize(props.addressType)}AddressLine1` as keyof IPersonalInfoErrors,
                            regexContainer.allow[`${props.addressType}_address`],
                            regexContainer.disallow[`${props.addressType}_address`],
                        );
                        if (!loqateAutocompleteToggle || (loqateAutocompleteToggle && !document.querySelector('.pcaselected:hover'))) {
                            validateFullAddress();
                        }
                    }}
                    ref={addressInputRef}
                    required
                    setValueCallback={(value: string) => {
                        const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                        if (containsInvalidCharacters) {
                            value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                        }

                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [FIELD_NAMES.ADDRESS]: { value: value, valid: true } }));
                    }}
                    useOutsideValidation={personalInfoForm[FIELD_NAMES.ADDRESS].valid}
                    useSuccessIcon
                    value={personalInfoForm[FIELD_NAMES.ADDRESS].value}
                    disclaimerMessage={props.disclaimerMessage}
                />
            </Wrapper>
            <Wrapper {...{ ...wrapper.wrapperProps, ...wrapper.aptWrapperProps }}>
                <ApartmentField key='ApartmentField'
                    addressType={props.addressType}
                    alternate
                    className='block'
                    error={!personalInfoForm[FIELD_NAMES.APTUNIT].valid}
                    errorText='Please enter valid Apt/Unit#'
                    id={`${props.addressType}AddressLine2`}
                    label='Apt/Unit #'
                    maxLength={100}
                    onBlurCallback={() => {
                        if (props.addressType === 'home') {
                            verifyAddressAndAptDoNotMatch() &&
                                validateField(
                                    FIELD_NAMES.APTUNIT,
                                    `${capitalize(props.addressType)}AddressLine2` as keyof IPersonalInfoErrors,
                                    regexContainer.allow.mailing_address,
                                    regexContainer.disallow.mailing_address,
                                    false,
                                    () => Number(personalInfoForm[FIELD_NAMES.APTUNIT]?.value?.length) <= 100,
                                );
                        } else { // mailing
                            verifyAddressAndAptDoNotMatch() &&
                                validateFullAddress();
                        }
                    }}
                    ref={aptRef}
                    setValueCallback={(value: string) => {
                        const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                        if (containsInvalidCharacters) {
                            value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                        }

                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [FIELD_NAMES.APTUNIT]: { value: value, valid: true } }));
                    }}
                    useOutsideValidation={personalInfoForm[FIELD_NAMES.APTUNIT].valid}
                    useSuccessIcon
                    value={personalInfoForm[FIELD_NAMES.APTUNIT].value}
                />
            </Wrapper>
            <Wrapper {...{ ...wrapper.wrapperProps, ...wrapper.cityWrapperProps }}>
                <CityField key='CityField'
                    addressType={props.addressType}
                    alternate
                    className='block'
                    error={!personalInfoForm[FIELD_NAMES.CITY].valid}
                    errorText={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}City`}
                    requiredMessage={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}City`}
                    id={`${props.addressType}AddressCity`}
                    label='City'
                    onBlurCallback={() => {
                        validateField(FIELD_NAMES.CITY, capitalize(FIELD_NAMES.CITY) as keyof IPersonalInfoErrors, regexContainer.city);
                        validateFullAddress();
                    }}
                    ref={cityRef}
                    required
                    setValueCallback={(value: string) => {
                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [FIELD_NAMES.CITY]: { value: value.replace(/[^a-zA-Z\s'-]/g, '').replace(/\s\s+/g, ' '), valid: true } }));
                    }}
                    useOutsideValidation={personalInfoForm[FIELD_NAMES.CITY].valid}
                    useSuccessIcon
                    value={personalInfoForm[FIELD_NAMES.CITY].value}
                />
            </Wrapper>
            <Wrapper {...{ ...wrapper.wrapperProps, ...wrapper.stateWrapperProps }}>
                {/* todo: we need to remove the class here and make this generic for all forms */}
                <div className='PersonalInformationForm__stateInputContainer'>
                    <StateField
                        addressType={props.addressType}
                        alternate
                        error={!personalInfoForm[FIELD_NAMES.STATE].valid}
                        errorText={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}State`}
                        id={`${props.addressType}AddressState`}
                        invalidErrorMessage={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}State`}
                        label='State'
                        mixed value={personalInfoForm[FIELD_NAMES.STATE].value}
                        name='State'
                        onBlurCallback={() => {
                            validateField(FIELD_NAMES.STATE, capitalize(FIELD_NAMES.STATE) as keyof IPersonalInfoErrors, regexContainer.state);
                            validateFullAddress();
                        }}
                        placeholder='Select a State'
                        ref={stateRef}
                        required
                        setValueCallback={(value: string) => {
                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [FIELD_NAMES.STATE]: { value: value, valid: true } }));
                        }}
                        useOutsideValidation={personalInfoForm[FIELD_NAMES.STATE].valid}
                        useSuccessIcon
                        isPrefill
                        resetToggle={stateResetToggle}
                    />
                </div>
                {personalInfoForm.mailingState.value === 'WI' && (
                    <p className='PersonalInformationForm__note'><strong>WISCONSIN: </strong>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.wiDisclaimer))}</p>
                )}
            </Wrapper>
            <Wrapper {...{ ...wrapper.wrapperProps, ...wrapper.zipWrapperProps }}>
                <ZipCodeField key='ZipCodeField'
                    addressType={props.addressType}
                    alternate
                    className='block'
                    error={!personalInfoForm[FIELD_NAMES.ZIP].valid}
                    errorText={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}ZIP Code`}
                    requiredMessage={`Please provide a valid ${props.labelType === 'home' ? 'Home Address ' : ''}ZIP Code`}
                    helperText='XXXXX'
                    id={`${props.addressType}AddressZip`}
                    label='ZIP Code'
                    onBlurCallback={() => {
                        validateField(FIELD_NAMES.ZIP, capitalize(FIELD_NAMES.ZIP + 'Code') as keyof IPersonalInfoErrors, regexContainer.zip_code);
                        if (personalInfoForm[`${props.addressType}Zip`].value.length === 5) {
                            validateFullAddress();
                        }
                    }}
                    ref={zipRef}
                    required
                    setValueCallback={(value: string) => {
                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [FIELD_NAMES.ZIP]: { value: value.replace(/[^0-9]/g, ''), valid: true } }));
                    }}
                    useOutsideValidation={personalInfoForm[FIELD_NAMES.ZIP].valid}
                    useSuccessIcon
                    value={personalInfoForm[FIELD_NAMES.ZIP].value}
                />
            </Wrapper>
            {showPopup &&
                <AddressSuggestionPopup
                    suggestedAddress={loqateResponse.suggestedAddress}
                    headline={props.suggestedAddressHeadline}
                    subHeadline={props.suggestedAddressSubHeadline}
                    acceptCtaText={props.suggestedAddressAcceptCtaText}
                    declineCtaText={props.suggestedAddressDeclineCtaText}
                    acceptCtaCallback={() => handleAcceptCta(props.addressType)}
                    declineCtaCallback={() => handleDeclineCta(props.addressType)}
                />}
        </>
    );
};

export default GroupedAddressFieldsHomePrimary;